import { useState } from 'react';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import {
  Button,
  Menu,
  MenuItem,
  MenuTrigger,
  Popover,
  SubmenuTrigger,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { clsx } from 'clsx';

import type { FieldOptions, FilterToken, Operator, ParsedToken } from '../../../types/QueryBuilder';
import { useQueryBuilderContext } from '../context';
import { FilterBarContext } from '../FilterBarContext';
import { LozengeFilter } from '../tokens/LozengeFilter';
import { getFocusableCells, removeToken, replaceToken, stringifyToken, tokenValueTexts } from '../utils';

import { FieldValuePanel, isMultiValue } from './FieldValuePanel';

import styles from './InlineFilterMenu.module.css';

type FilterTokenNode = Extract<ParsedToken, { type: 'FILTER' }>;

interface InlineFilterMenuProps {
  fieldOptions?: FieldOptions[];
  omitFields?: string[];
  pinnedFields?: string[];
  /** Applies multi-value selections immediately without closing the inline filter menu. */
  menuMultiSelect?: boolean;
  clearAllLabel?: string;
  className?: string;
  /** Renders the lozenges non-interactive and hides the remove buttons and the Filter trigger. */
  readonly?: boolean;
  /** Renders the Filter trigger visible but disabled, for when filtering isn't available yet. */
  menuDisabled?: boolean;
  /** When false, a single selected value keeps its complete display label. */
  truncateSingleValue?: boolean;
}

function operatorFor(field: FieldOptions): Operator {
  return field.allowedOperators?.[0] ?? '=';
}

function toFilterToken(token: FilterTokenNode): FilterToken {
  const texts = tokenValueTexts(token);
  return { field: token.field, operator: token.operator, value: texts.length === 1 ? texts[0] : texts };
}

export function InlineFilterMenu({
  fieldOptions = [],
  omitFields,
  pinnedFields,
  menuMultiSelect = false,
  clearAllLabel,
  className,
  readonly = false,
  menuDisabled = false,
  truncateSingleValue = true,
}: InlineFilterMenuProps) {
  const { query, parsedQuery, dispatch, activeValueField } = useQueryBuilderContext();
  const [isOpen, setIsOpen] = useState(false);

  const isHidden = (fieldKey: string) => !!omitFields?.includes(fieldKey);
  const filterTokens = parsedQuery.filter((t): t is FilterTokenNode => t.type === 'FILTER' && !isHidden(t.field));
  const fields = fieldOptions.filter((option) => !isHidden(option.field));

  const pinnedKeys = (pinnedFields ?? []).filter((fieldKey) => fields.some((f) => f.field === fieldKey));
  const pinnedSet = new Set(pinnedKeys);
  const unpinnedTokens = filterTokens.filter((token) => !pinnedSet.has(token.field));
  const hasLozenges = pinnedKeys.length > 0 || unpinnedTokens.length > 0;

  const setFieldKeys = new Set(filterTokens.map((token) => token.field));
  const menuFields = fields.filter(
    (field) =>
      (!setFieldKeys.has(field.field) || (menuMultiSelect && isOpen && field.field === activeValueField)) &&
      !pinnedSet.has(field.field),
  );

  const writeFieldValues = (
    fieldKey: string,
    values: Array<string | number | boolean>,
    existingToken?: FilterTokenNode,
  ) => {
    const fieldOption = fields.find((o) => o.field === fieldKey);
    const existing =
      existingToken ?? parsedQuery.find((t): t is FilterTokenNode => t.type === 'FILTER' && t.field === fieldKey);
    let nextQuery: string;
    if (values.length === 0) {
      nextQuery = existing ? removeToken(query, existing) : query;
    } else {
      const replacement = stringifyToken({
        type: 'FILTER',
        field: fieldKey,
        operator: existing ? existing.operator : fieldOption ? operatorFor(fieldOption) : '=',
        value: values,
        text: '',
        location: { start: { offset: 0 }, end: { offset: 0 } },
      });
      nextQuery = existing
        ? replaceToken(query, existing, replacement)
        : query
          ? `${query} ${replacement}`
          : replacement;
    }
    dispatch({ type: 'SET_QUERY', query: nextQuery });
  };

  const applyFromMenu = (field: FieldOptions, values: Array<string | number | boolean>) => {
    writeFieldValues(field.field, values);
    setIsOpen(false);
  };

  const currentValuesFor = (fieldKey: string): Array<string | number | boolean> => {
    const token = filterTokens.find((t) => t.field === fieldKey);
    if (!token) {
      return [];
    }
    return tokenValueTexts(token);
  };

  const renderLozenge = (fieldKey: string, token: FilterTokenNode | undefined, pinned: boolean, index?: number) => {
    const fieldOption = fields.find((f) => f.field === fieldKey);
    const lozengeKey = pinned ? `inline-${fieldKey}` : `inline-${fieldKey}-${index}`;
    return (
      <LozengeFilter
        key={lozengeKey}
        tokenKey={lozengeKey}
        variant="inline"
        truncateSingleValue={truncateSingleValue}
        value={
          token
            ? toFilterToken(token)
            : { field: fieldKey, operator: fieldOption ? operatorFor(fieldOption) : '=', values: [] }
        }
        onChange={() => {}}
        onRemove={() => token && dispatch({ type: 'DELETE_TOKEN', token })}
        onEditValues={(values) => writeFieldValues(fieldKey, values, token)}
        removable={!pinned && !readonly}
        options={fieldOptions}
        parsedToken={token}
      />
    );
  };

  return (
    <div className={clsx(styles.root, className)} data-qb-nav-root>
      {hasLozenges && (
        <FilterBarContext.Provider value={{ readonly, gridState: null, showTypeLabels: false }}>
          {pinnedKeys.map((fieldKey) =>
            renderLozenge(
              fieldKey,
              filterTokens.find((t) => t.field === fieldKey),
              true,
            ),
          )}
          {unpinnedTokens.map((token, index) => renderLozenge(token.field, token, false, index))}
        </FilterBarContext.Provider>
      )}
      {!readonly && (menuFields.length > 0 || menuDisabled) && (
        <MenuTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
          <TooltipTrigger delay={500} isDisabled={isOpen}>
            <Button
              className={styles.trigger}
              aria-label={msg('queryBuilder.inlineFilterMenu.filter')}
              isDisabled={menuDisabled}
              onKeyDown={(e) => {
                if (e.key !== 'ArrowLeft') {
                  return;
                }
                const navRoot: HTMLElement | null = e.currentTarget.closest('[data-qb-nav-root]');
                if (!navRoot) {
                  return;
                }
                const cells = getFocusableCells(navRoot);
                const idx = cells.indexOf(e.currentTarget);
                if (idx > 0) {
                  e.preventDefault();
                  cells[idx - 1].focus();
                }
              }}
            >
              <Icon name="filter-list" size="small" aria-hidden />
              {!hasLozenges && (
                <span>
                  <Message id="queryBuilder.inlineFilterMenu.filter" />
                </span>
              )}
            </Button>
            <Tooltip>
              <Message id="queryBuilder.inlineFilterMenu.addFilter" />
            </Tooltip>
          </TooltipTrigger>
          <Popover>
            <Menu className={styles.fieldMenu}>
              {menuFields.map((field) => (
                <SubmenuTrigger key={field.field}>
                  <MenuItem id={field.field}>
                    <Text slot="label" className={styles.fieldLabel}>
                      {field.iconName && <Icon name={field.iconName} size="small" aria-hidden />}
                      <span>{field.displayValue ?? field.field}</span>
                    </Text>
                  </MenuItem>
                  <Popover className={styles.valuePopover}>
                    <FieldValuePanel
                      field={field}
                      initialValues={currentValuesFor(field.field)}
                      onApply={(values) => applyFromMenu(field, values)}
                      onValuesChange={(values) => writeFieldValues(field.field, values)}
                      onClose={() => setIsOpen(false)}
                      onDismiss={() => setIsOpen(false)}
                      immediate={menuMultiSelect && isMultiValue(field)}
                      singleSelect={menuMultiSelect ? !isMultiValue(field) : true}
                    />
                  </Popover>
                </SubmenuTrigger>
              ))}
            </Menu>
          </Popover>
        </MenuTrigger>
      )}
      {!readonly && clearAllLabel && query.trim() && (
        <Button
          variant="minimal"
          size="small"
          onPress={() => {
            dispatch({ type: 'UPDATE_QUERY', query: '' });
          }}
        >
          {clearAllLabel}
        </Button>
      )}
    </div>
  );
}
